import { useEffect, useRef, useState } from 'react';

const AnimatedNumber = ({ value, suffix = '', prefix = '' }) => {
  const [displayed, setDisplayed] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) {
      setDisplayed(value);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 1200;
          const start = Date.now();
          const animate = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplayed(Math.round(value * eased));
            if (progress < 1) requestAnimationFrame(animate);
          };
          animate();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="kpi-number text-2xl sm:text-3xl lg:text-4xl text-white whitespace-nowrap">
      {prefix}{displayed.toLocaleString('sl-SI')}{suffix}
    </span>
  );
};

export const KpiStrip = ({ kpis, cols }) => {
  const colClass = cols === 3 ? 'grid-cols-3' : cols === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4';
  return (
    <div className={`grid ${colClass} gap-4`}>
      {kpis.map((kpi, i) => (
        <div key={i} className="text-center sm:text-left">
          {kpi.staticValue
            ? <span className="kpi-number text-lg sm:text-xl lg:text-2xl text-white leading-tight tracking-tight">{kpi.staticValue}</span>
            : <AnimatedNumber value={kpi.value} suffix={kpi.suffix || ''} prefix={kpi.prefix || ''} />
          }
          <p className="font-mono text-xs text-white/50 mt-1 uppercase tracking-wider">
            {kpi.label}
          </p>
        </div>
      ))}
    </div>
  );
};
